/**
 * Data tools (website roadmap S40; Account → Data): a backup file of everything Omni-Torn keeps for a player, restoring
 * one, deleting kept history by range, and the CSV every website table can export. Nothing new is stored and nothing is
 * asked of Torn: the backup is what the server already keeps for that player (their own history streams, their history
 * snapshots and company days, their synced settings and lists — alert rules among them — and their own pricelist); a
 * restore puts back only what passes the same checks as a fresh upload (own-history.ts `cleanOwnUpload`, web.ts
 * `cleanSnapshot`, company.ts `cleanCompanyDay`) and never overwrites a row the server already has. Deleting by range is
 * the player removing their own data (owner's rule "No age limits": stored data goes only when the player removes it).
 *
 * The file: NDJSON (one JSON value a line), gzipped (`.ndjson.gz`), written in this order —
 *   { type: 'header', kind: 'omni-torn-backup', format: 1, playerId, name, createdAt }
 *   { type: 'prefs', prefs }                       — for reference only (a restore never changes your choices or terms)
 *   { type: 'settings', doc } / { type: 'lists', doc } / { type: 'pricelist', list }   (null: none kept)
 *   { type: 'spans', spans: { <stream key>: Span[] } }                                  — for reference only
 *   { type: 'snapshots', items: Snapshot[] } … { type: 'company', items: CompanyDay[] } …
 *   { type: 'log' | 'attacks' | 'races', entries: { <Torn's id>: <Torn's entry> } } …   (BACKUP_BATCH a line at most)
 *   { type: 'end', counts: { log, attacks, races, snapshots, company } }                 — a file without it is cut short
 */
import { cleanCompanyDay } from './company';
import { csvCell } from './log-kit';
import { cleanOwnUpload, isOwnStream, OWN_STREAMS, ownEntryTime, type OwnStream } from './own-history';
import { cleanPricelist, type PricelistEntry } from './pricelists';
import { cleanSnapshot } from './web';

export const BACKUP_KIND = 'omni-torn-backup';
export const BACKUP_FORMAT = 1;
/** Entries (or snapshots, or company days) per line of the file — also what a restore sends at once. */
export const BACKUP_BATCH = 500;
/** One line of the file may be at most this long (a batch of Torn's entries is far smaller). */
export const BACKUP_LINE_MAX = 4_000_000;

/** What can be backed up, restored and deleted by range: the own-history streams plus history snapshots and company days. */
export type DataStream = OwnStream | 'snapshots' | 'company';
export const DATA_STREAMS: readonly DataStream[] = ['log', 'attacks', 'races', 'items', 'snapshots', 'company'];
export const DATA_STREAM_NAMES: Readonly<Record<DataStream, string>> = {
  log: 'Activity log',
  attacks: 'Fights',
  races: 'Races',
  items: 'Item stats',
  snapshots: 'Stats history (snapshots)',
  company: 'Company days',
};
export function isDataStream(s: unknown): s is DataStream {
  return isOwnStream(s) || s === 'snapshots' || s === 'company';
}

/** The settings document as the server keeps it (settings-docs.ts). */
export interface BackupSettingsShape {
  version: 2;
  general: Record<string, boolean | number | string>;
  features: Record<string, { enabled?: boolean; options?: Record<string, boolean | number | string> }>;
}
export interface BackupDoc<T> {
  value: T;
  updatedAt: number;
}
export interface BackupPricelist {
  open: boolean;
  about: string;
  items: PricelistEntry[];
  updatedAt: number;
}

export type BackupLine =
  | { type: 'header'; kind: typeof BACKUP_KIND; format: number; playerId: number; name: string; createdAt: number; sig?: string }
  | { type: 'prefs'; prefs: unknown }
  | { type: 'settings'; doc: { settings: unknown; updatedAt: number; updatedBy?: string } | null }
  | { type: 'lists'; doc: { lists: unknown; updatedAt: number; updatedBy?: string } | null }
  | { type: 'pricelist'; list: BackupPricelist | null }
  | { type: 'spans'; spans: unknown }
  | { type: 'snapshots'; items: unknown[] }
  | { type: 'company'; items: unknown[] }
  | { type: OwnStream; entries: Record<string, unknown> }
  | { type: 'end'; counts: Record<DataStream, number> };

/** One line of the file as JSON text (with its newline). */
export const backupLine = (l: BackupLine): string => `${JSON.stringify(l)}\n`;

/** A backup's file name: omni-torn-backup-<id>-<YYYY-MM-DD>.ndjson.gz */
export const backupFileName = (playerId: number, at: number): string => `omni-torn-backup-${playerId}-${new Date(at).toISOString().slice(0, 10)}.ndjson.gz`;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Nothing in a backup is older than Torn itself: dates before 2003 are refused (a restore can't backdate rows). */
export const BACKUP_OLDEST = Date.UTC(2003, 0, 1);

/** When a snapshot or company day happened (ms), or null. */
function itemTime(v: unknown): number | null {
  const t = isRecord(v) ? Number(v.at) : NaN;
  return Number.isFinite(t) && t >= BACKUP_OLDEST ? t : null;
}

/**
 * What the server signs in a backup's header (HMAC-SHA256 with its own backup secret — routes/data.ts): a restore puts
 * history rows back only from a file this server made for this player; settings and lists are the player's own choice.
 */
export const backupSignedText = (h: { format: number; playerId: number; createdAt: number }): string => `${BACKUP_KIND}:${h.format}:${h.playerId}:${h.createdAt}`;

/** A snapshot from a backup as it may be kept again (its own time), or null. */
export function restoredSnapshot(raw: unknown, now: number, bankUntil: boolean): ReturnType<typeof cleanSnapshot> {
  const at = itemTime(raw);
  if (at === null || at > now + 60_000) return null;
  return cleanSnapshot(raw, Math.round(at), { bankUntil });
}

/** A company day from a backup as it may be kept again, or null. */
export function restoredCompanyDay(raw: unknown, now: number): ReturnType<typeof cleanCompanyDay> {
  const d = cleanCompanyDay(raw, now);
  return d && d.at >= BACKUP_OLDEST ? d : null;
}

// ---------------------------------------------------------------- checking a file before restoring it

export interface StreamTally {
  count: number;
  oldest: number | null;
  newest: number | null;
}

export interface BackupCheck {
  header: Extract<BackupLine, { type: 'header' }> | null;
  settings: { settings: BackupSettingsShape; updatedAt: number } | null;
  lists: { lists: Record<string, unknown>; updatedAt: number } | null;
  pricelist: BackupPricelist | null;
  tally: Record<DataStream, StreamTally>;
  /** The file's own end line (its counts match what was read). */
  complete: boolean;
  /** Why the file can't be restored (the first problem found), or null. */
  problem: string | null;
  lines: number;
}

const emptyTally = (): Record<DataStream, StreamTally> => Object.fromEntries(DATA_STREAMS.map((s) => [s, { count: 0, oldest: null, newest: null }])) as Record<DataStream, StreamTally>;

function tallyAdd(t: StreamTally, time: number | null): void {
  t.count++;
  if (time === null) return;
  t.oldest = t.oldest === null ? time : Math.min(t.oldest, time);
  t.newest = t.newest === null ? time : Math.max(t.newest, time);
}

/**
 * Reads a backup line by line (`feed`), checking each as the server will (own data only, Torn's shapes) — so the preview
 * says what a restore would do and refuses a file that isn't this player's before anything is sent.
 */
export class BackupChecker {
  readonly check: BackupCheck = { header: null, settings: null, lists: null, pricelist: null, tally: emptyTally(), complete: false, problem: null, lines: 0 };

  constructor(
    private readonly playerId: number,
    private readonly now: number,
  ) {}

  #fail(why: string): void {
    this.check.problem ??= `Line ${this.check.lines}: ${why}`;
  }

  /** One line of the file (blank lines are skipped). Answers false once the file is refused. */
  feed(text: string): boolean {
    if (this.check.problem) return false;
    const s = text.trim();
    if (!s) return true;
    this.check.lines++;
    if (s.length > BACKUP_LINE_MAX) return this.#fail('too long.'), false;
    let v: unknown;
    try {
      v = JSON.parse(s);
    } catch {
      return this.#fail('not JSON — is this an Omni-Torn backup file?'), false;
    }
    if (!isRecord(v) || typeof v.type !== 'string') return this.#fail('not an Omni-Torn backup line.'), false;
    const c = this.check;
    if (!c.header) {
      if (v.type !== 'header' || v.kind !== BACKUP_KIND) return this.#fail('this isn’t an Omni-Torn backup file.'), false;
      if (Number(v.format) > BACKUP_FORMAT) return this.#fail('made by a newer Omni-Torn — update first.'), false;
      if (Number(v.playerId) !== this.playerId) return this.#fail(`this backup is of another player [${Number(v.playerId) || '?'}] — only your own data can be restored.`), false;
      c.header = { type: 'header', kind: BACKUP_KIND, format: Number(v.format), playerId: Number(v.playerId), name: String(v.name ?? '').slice(0, 40), createdAt: Number(v.createdAt) || 0, ...(typeof v.sig === 'string' ? { sig: v.sig.slice(0, 128) } : {}) };
      return true;
    }
    if (c.complete) return this.#fail('lines after the end.'), false;
    switch (v.type) {
      case 'prefs':
      case 'spans':
        return true; // for reference only
      case 'settings': {
        const d = v.doc;
        if (d === null) return true;
        const settings = isRecord(d) ? cleanSettingsShape(d.settings) : null;
        if (!settings) return this.#fail('the settings aren’t Omni-Torn settings.'), false;
        c.settings = { settings, updatedAt: Number((d as Record<string, unknown>).updatedAt) || 0 };
        return true;
      }
      case 'lists': {
        const d = v.doc;
        if (d === null) return true;
        if (!isRecord(d) || !isRecord(d.lists) || Object.keys(d.lists).some((k) => !/^omnitorn\.[\w.:-]{1,60}$/.test(k))) return this.#fail('the lists aren’t Omni-Torn lists.'), false;
        c.lists = { lists: d.lists, updatedAt: Number(d.updatedAt) || 0 };
        return true;
      }
      case 'pricelist': {
        if (v.list === null) return true;
        const p = cleanPricelist(v.list);
        if (!p.ok) return this.#fail(`the pricelist: ${p.message}`), false;
        c.pricelist = { ...p.list, updatedAt: Number((v.list as Record<string, unknown>).updatedAt) || 0 };
        return true;
      }
      case 'snapshots':
      case 'company': {
        if (!Array.isArray(v.items) || v.items.length > BACKUP_BATCH * 2) return this.#fail(`the ${v.type} aren’t a list.`), false;
        for (const it of v.items) {
          const ok = v.type === 'snapshots' ? restoredSnapshot(it, this.now, true) : restoredCompanyDay(it, this.now);
          if (!ok) return this.#fail(`a ${v.type === 'snapshots' ? 'stats snapshot' : 'company day'} isn’t usable.`), false;
          tallyAdd(c.tally[v.type], itemTime(it));
        }
        return true;
      }
      case 'end': {
        const counts = isRecord(v.counts) ? v.counts : {};
        for (const s of DATA_STREAMS) if (Number(counts[s] ?? 0) !== c.tally[s].count) return this.#fail(`the file says ${Number(counts[s] ?? 0)} ${s} but holds ${c.tally[s].count} — it was changed or cut short.`), false;
        c.complete = true;
        return true;
      }
      default: {
        if (!isOwnStream(v.type)) return this.#fail(`unknown kind of line (${String(v.type).slice(0, 20)}).`), false;
        const u = cleanOwnUpload(v.type, { entries: v.entries }, this.playerId, this.now);
        if ('problem' in u) return this.#fail(u.problem), false;
        for (const e of Object.values(u.entries)) {
          const t = ownEntryTime(v.type, e);
          if (t !== null && t < BACKUP_OLDEST) return this.#fail('an entry is dated before Torn began.'), false;
          tallyAdd(c.tally[v.type], t);
        }
        return true;
      }
    }
  }

  /** After the last line: the result (a file without its end line is refused — it was cut short). */
  finish(): BackupCheck {
    if (!this.check.problem && !this.check.header) this.check.problem = 'The file is empty.';
    if (!this.check.problem && !this.check.complete) this.check.problem = 'The file ends too early (no end line) — it was cut short. Download the backup again.';
    return this.check;
  }
}

/** The settings document's shape (the server cleans it again): version 2 with `general` and `features` objects. */
export function cleanSettingsShape(raw: unknown): BackupSettingsShape | null {
  if (!isRecord(raw) || raw.version !== 2 || !isRecord(raw.general) || (raw.features !== undefined && !isRecord(raw.features))) return null;
  const plain = (x: unknown): x is boolean | number | string => typeof x === 'boolean' || (typeof x === 'number' && Number.isFinite(x)) || typeof x === 'string';
  const values = (o: Record<string, unknown>) => Object.fromEntries(Object.entries(o).filter(([, x]) => plain(x))) as Record<string, boolean | number | string>;
  const features: BackupSettingsShape['features'] = {};
  for (const [id, f] of Object.entries((raw.features as Record<string, unknown> | undefined) ?? {})) {
    if (!isRecord(f)) continue;
    const e: { enabled?: boolean; options?: Record<string, boolean | number | string> } = {};
    if (typeof f.enabled === 'boolean') e.enabled = f.enabled;
    if (isRecord(f.options)) e.options = values(f.options);
    features[id] = e;
  }
  return { version: 2, general: values(raw.general), features };
}

// ---------------------------------------------------------------- merging settings, lists and the pricelist

/** Where the two copies differ: keep this server's ("server", the default — never overwritten silently) or the file's. */
export type Prefer = 'server' | 'file';

export interface MergeSummary {
  /** In the file only: added. */
  added: number;
  /** In both, different: `prefer` decides; listed so the preview can show them. */
  differ: string[];
  same: number;
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** The settings joined: what only the file has is added; where both differ, `prefer` wins. */
export function mergeSettings(server: BackupSettingsShape | null, file: BackupSettingsShape, prefer: Prefer = 'server'): { merged: BackupSettingsShape; summary: MergeSummary } {
  const sum: MergeSummary = { added: 0, differ: [], same: 0 };
  const base: BackupSettingsShape = server ? structuredClone(server) : { version: 2, general: {}, features: {} };
  const put = (path: string, has: boolean, cur: unknown, val: unknown, set: () => void) => {
    if (!has) {
      sum.added++;
      set();
    } else if (same(cur, val)) sum.same++;
    else {
      sum.differ.push(path);
      if (prefer === 'file') set();
    }
  };
  for (const [k, v] of Object.entries(file.general)) put(`general.${k}`, k in base.general, base.general[k], v, () => (base.general[k] = v));
  for (const [id, f] of Object.entries(file.features)) {
    const cur = (base.features[id] ??= {});
    if (f.enabled !== undefined) put(`${id}.enabled`, cur.enabled !== undefined, cur.enabled, f.enabled, () => (cur.enabled = f.enabled));
    for (const [k, v] of Object.entries(f.options ?? {})) {
      const opts = (cur.options ??= {});
      put(`${id}.${k}`, k in opts, opts[k], v, () => (opts[k] = v));
    }
    if (cur.options && !Object.keys(cur.options).length) delete cur.options;
    if (!Object.keys(cur).length) delete base.features[id];
  }
  return { merged: base, summary: sum };
}

export interface ListMerge {
  key: string;
  /** add: only in the file; same; merged: joined (entries added); differ: both changed the same entries (`prefer` decides). */
  action: 'add' | 'same' | 'merged' | 'differ';
  added: number;
  differ: number;
}

/** Items of a list that carry their own id (alert rules, saved targets…) are matched by it; others by their whole value. */
function itemKey(x: unknown): string {
  if (isRecord(x) && (typeof x.id === 'string' || typeof x.id === 'number')) return `id:${String(x.id)}`;
  return `v:${JSON.stringify(x)}`;
}

/** One list joined: arrays by item (id or value), objects by key, anything else whole. */
function mergeOne(server: unknown, file: unknown, prefer: Prefer): { value: unknown; added: number; differ: number } {
  if (Array.isArray(server) && Array.isArray(file)) {
    const out = [...server];
    const at = new Map(out.map((x, i) => [itemKey(x), i]));
    let added = 0;
    let differ = 0;
    for (const x of file) {
      const k = itemKey(x);
      const i = at.get(k);
      if (i === undefined) {
        at.set(k, out.length);
        out.push(x);
        added++;
      } else if (!same(out[i], x)) {
        differ++;
        if (prefer === 'file') out[i] = x;
      }
    }
    return { value: out, added, differ };
  }
  if (isRecord(server) && isRecord(file)) {
    const out: Record<string, unknown> = { ...server };
    let added = 0;
    let differ = 0;
    for (const [k, v] of Object.entries(file)) {
      if (!(k in out)) {
        out[k] = v;
        added++;
      } else if (!same(out[k], v)) {
        differ++;
        if (prefer === 'file') out[k] = v;
      }
    }
    return { value: out, added, differ };
  }
  if (same(server, file)) return { value: server, added: 0, differ: 0 };
  return { value: prefer === 'file' ? file : server, added: 0, differ: 1 };
}

/** The lists joined, list by list. */
export function mergeLists(server: Record<string, unknown> | null, file: Record<string, unknown>, prefer: Prefer = 'server'): { merged: Record<string, unknown>; lists: ListMerge[] } {
  const merged: Record<string, unknown> = { ...(server ?? {}) };
  const lists: ListMerge[] = [];
  for (const [key, v] of Object.entries(file)) {
    if (!(key in merged) || merged[key] === null) {
      if (v === null) continue;
      merged[key] = v;
      lists.push({ key, action: 'add', added: Array.isArray(v) ? v.length : isRecord(v) ? Object.keys(v).length : 1, differ: 0 });
      continue;
    }
    const m = mergeOne(merged[key], v, prefer);
    merged[key] = m.value;
    lists.push({ key, action: m.differ ? 'differ' : m.added ? 'merged' : 'same', added: m.added, differ: m.differ });
  }
  return { merged, lists };
}

/** The pricelist joined item by item (the server's open/about stay unless it has none, or `prefer` is the file). */
export function mergePricelist(server: Omit<BackupPricelist, 'updatedAt'> | null, file: Omit<BackupPricelist, 'updatedAt'>, prefer: Prefer = 'server'): { merged: Omit<BackupPricelist, 'updatedAt'>; summary: MergeSummary } {
  const sum: MergeSummary = { added: 0, differ: [], same: 0 };
  if (!server) return { merged: { open: file.open, about: file.about, items: [...file.items] }, summary: { ...sum, added: file.items.length } };
  const items = new Map(server.items.map((e) => [e.itemId, e]));
  for (const e of file.items) {
    const cur = items.get(e.itemId);
    if (!cur) {
      items.set(e.itemId, e);
      sum.added++;
    } else if (cur.mode === e.mode && cur.value === e.value && cur.note === e.note) sum.same++;
    else {
      sum.differ.push(String(e.itemId));
      if (prefer === 'file') items.set(e.itemId, e);
    }
  }
  const head = prefer === 'file' ? { open: file.open, about: file.about } : { open: server.open, about: server.about };
  return { merged: { ...head, items: [...items.values()] }, summary: sum };
}

// ---------------------------------------------------------------- delete by range

/** A date range from two "YYYY-MM-DD" days (Torn time, UTC; both days included) as ms [from, to). Null when not a range. */
export function dayRange(fromDay: string, toDay: string): { from: number; to: number } | null {
  const a = Date.parse(`${fromDay}T00:00:00Z`);
  const b = Date.parse(`${toDay}T00:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fromDay) || !/^\d{4}-\d{2}-\d{2}$/.test(toDay) || !Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  return { from: a, to: b + 86_400_000 };
}

/** A range as the server takes it (ms, from < to, not before 2000), or null. */
export function cleanRange(fromRaw: unknown, toRaw: unknown): { from: number; to: number } | null {
  const from = Number(fromRaw);
  const to = Number(toRaw);
  if (!Number.isFinite(from) || !Number.isFinite(to) || from < 0 || to <= from || to > 8.64e15) return null;
  return { from: Math.floor(from), to: Math.ceil(to) };
}

// ---------------------------------------------------------------- CSV from any table (Excel-friendly)

/**
 * A table cell's text as a CSV value a spreadsheet reads well: plain numbers as numbers ("$1,234,567" → 1234567,
 * "−5" → -5); everything else as its text (csvCell quotes it and defuses formulas).
 */
export function csvValue(text: string): string | number {
  const t = text.replace(/−/g, '-').replace(/\s+/g, ' ').trim();
  const m = /^([+-])?\$?([+-])?(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?$/.exec(t);
  if (m && !(m[1] && m[2]) && !/^0\d/.test(m[3]!)) {
    const n = Number(`${m[1] ?? m[2] ?? ''}${m[3]!.replace(/,/g, '')}${m[4] ?? ''}`);
    if (Number.isFinite(n) && Math.abs(n) < 1e15) return n;
  }
  return text.replace(/\s+/g, ' ').trim();
}

/** Rows as CSV text Excel opens cleanly: a UTF-8 byte-order mark first, CRLF line ends. */
export function excelCsv(header: readonly string[], rows: ReadonlyArray<ReadonlyArray<string | number | null | undefined>>): string {
  const lines = header.length ? [header, ...rows] : rows;
  return `﻿${lines.map((r) => r.map(csvCell).join(',')).join('\r\n')}\r\n`;
}

/** Every kind of line, for the server's writer: the own-history streams in order. */
export const BACKUP_STREAMS: readonly OwnStream[] = OWN_STREAMS;
